/// <amd-module name="Artifacts/Npm/redux/Sagas" />

import {
  BatchDeprecateData,
  NpmBatchOperationType,
  NpmPackagesBatchRequest,
} from "Artifacts/Npm/Contracts/Npm";
import { IDeprecateActionPayload, NpmActionTypes } from "Artifacts/Npm/redux/Actions";
import { INpmDataService } from "Artifacts/Npm/services/NpmDataService";
import { INpmRestApiUrlGeneratorService } from "Artifacts/Npm/services/NpmRestApiUrlGeneratorService";
import { PackageActions } from "Artifacts/Package/redux/Actions";
import {
  BatchGenerator,
  IBatchIterator,
  TransformingBatchGenerator,
} from "Artifacts/Package/services/BatchHandling";
import { PerfScenarios } from "Artifacts/Root/Constants";
import { getFeedNameWithNewView } from "Artifacts/Root/helpers/FeedHelpers";
import { FeedPerformanceHelper } from "Artifacts/Root/helpers/FeedPerformanceHelper";
import {
  IDefaultActionPayload,
  IDeleteActionPayload,
  IPromoteActionPayload,
  IRestoreActionPayload,
  IUpstreamOverrideActionPayload,
} from "Artifacts/Root/Protocols/types/ICommandActionPayloads";
import { RootActions } from "Artifacts/Root/redux/Actions";
import { all, call, getContext, put, takeEvery } from "Artifacts/Root/Saga";
import { IPackageVersionIdentity } from "Artifacts/Root/types";
import {
  MinimalPackageDetails,
  UpstreamingBehavior,
  UpstreamVersionVisibility,
} from "Artifacts/Root/types/PackagingShared";
import { SagaIterator } from "redux-saga";
import { ActionWithPayload } from "redux/Utils/ActionHelper";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssScenario } from "VSS/Platform/Performance";

export function* rootSaga(): SagaIterator {
  yield takeEvery(NpmActionTypes.Deprecate, deprecate);
  yield takeEvery(NpmActionTypes.Download, download);
  yield takeEvery(NpmActionTypes.Promote, promote);
  yield takeEvery(NpmActionTypes.Restore, restore);
  yield takeEvery(NpmActionTypes.PermanentDelete, permanentDelete);
  yield takeEvery(NpmActionTypes.SetUpstreamOverride, setUpstreamOverride);
  yield takeEvery(NpmActionTypes.SoftDelete, softDelete);
}

export function* deprecate(
  action: ActionWithPayload<NpmActionTypes.Deprecate, IDeprecateActionPayload>,
): Generator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  let perfScenario: IVssScenario | undefined;
  try {
    yield put(RootActions.deprecateCommandStarted());
    const { feed, versions, deprecateMessage, onSuccessCallback } = action.payload;

    const dataService = pageContext.getService<INpmDataService>("INpmDataService");

    const isSingleVersion = versions.length === 1;
    perfScenario = FeedPerformanceHelper.startScenario(
      pageContext,
      isSingleVersion
        ? PerfScenarios.PackageVersionDeprecated
        : PerfScenarios.MultiplePackageVersionsDeprecated,
    );

    const npmPackageDetails: MinimalPackageDetails[] = [];

    versions.forEach((v) => {
      npmPackageDetails.push({
        id: v.name,
        version: v.version,
      });
    });

    const deprecatePackageVersionDetails = {
      data: { message: deprecateMessage || "" } as BatchDeprecateData,
      operation: NpmBatchOperationType.Deprecate,
      packages: npmPackageDetails,
    } as NpmPackagesBatchRequest;

    yield call(
      [dataService, dataService.updatePackages],
      deprecatePackageVersionDetails,
      feed.id,
      feed.project?.id,
    );

    FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    yield put(RootActions.deprecateCommandCompleted());
    yield call(onSuccessCallback, versions, deprecateMessage);
    yield undefined;
  } catch (error) {
    if (perfScenario) {
      FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    }
    yield put(RootActions.deprecateCommandFailed(error.message));
  }
}

export function* download(
  action: ActionWithPayload<NpmActionTypes.Download, IDefaultActionPayload>,
): Generator {
  const { feed, versions } = action.payload;

  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  const service = pageContext.getService<INpmRestApiUrlGeneratorService>(
    "INpmRestApiUrlGeneratorService",
  );

  const results = (yield all(
    versions.map((v) => {
      return call(
        [service, service.getDownloadContentUrl],
        getFeedNameWithNewView(feed.id, feed.selectedViewId),
        v.name,
        v.normalizedVersion,
        feed && feed.project != null,
        feed.accountMetadata.publicAccessMapping,
      );
    }),
  ) as unknown) as string[];

  results.forEach((downloadPackageUrl) => {
    // The first time the user clicks the donwload button, they will get redirected to SPS for authentication.
    // If we hit the download url in the existing window, when the client gets redirected, the window will be stuck on SPS.
    // So we need to open a new window to download the package.
    const newWindow = window.open(downloadPackageUrl as string, "_blank");
    if (newWindow != null) {
      newWindow.opener = null;
    }
  });
}

export function* promote(
  action: ActionWithPayload<NpmActionTypes.Promote, IPromoteActionPayload>,
): Generator {
  try {
    yield put(RootActions.promoteCommandStarted());
    const { feed, versions, viewId, onSuccessCallback } = action.payload;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const protocolDataService = pageContext.getService<INpmDataService>("INpmDataService");
    const iterator: IBatchIterator<MinimalPackageDetails[]> = new BatchGenerator(
      versions.map((v) => {
        return {
          id: v.name,
          version: v.version,
        } as MinimalPackageDetails;
      }),
      protocolDataService.batchSize,
    );

    yield call(
      [protocolDataService, protocolDataService.batchPromotePackages],
      feed,
      viewId,
      iterator,
    );

    // retrieve the view so the name can be applied on the selected versions.
    const view = feed.views.find((v) => v.id === viewId);

    yield put(RootActions.promoteCommandCompleted());
    yield call(onSuccessCallback, versions, view?.name);
  } catch (error) {
    yield put(RootActions.promoteCommandFailed(error.message));
  }
}

export function* restore(
  action: ActionWithPayload<NpmActionTypes.Restore, IRestoreActionPayload>,
): Generator {
  yield put(RootActions.restoreCommandStarted());
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  let perfScenario: IVssScenario | undefined;
  try {
    const { feed, versions, onSuccessCallback } = action.payload;

    const isSingleVersion = versions.length === 1;
    perfScenario = FeedPerformanceHelper.startScenario(
      pageContext,
      isSingleVersion
        ? PerfScenarios.RecycleBinPackageVersionRestore
        : PerfScenarios.MultiplePackageVersionsRestoredToFeed,
    );

    const dataService = pageContext.getService<INpmDataService>("INpmDataService");

    const iterator = new TransformingBatchGenerator(
      versions,
      dataService.batchSize,
      (packageVersion: IPackageVersionIdentity) => {
        return {
          id: packageVersion.name,
          version: packageVersion.version,
        };
      },
    );

    const projectId = feed.project ? feed.project.id : "";

    yield call([dataService, dataService.batchRestoreToFeed], feed.id, iterator, projectId);

    FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    yield put(RootActions.restoreCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    if (perfScenario) {
      FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    }
    yield put(RootActions.restoreCommandFailed(error.message));
  }
}

export function* permanentDelete(
  action: ActionWithPayload<NpmActionTypes.PermanentDelete, IDeleteActionPayload>,
): Generator {
  const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
  let perfScenario: IVssScenario | undefined;
  try {
    yield put(RootActions.permDeleteCommandStarted());
    const { feed, versions, onSuccessCallback } = action.payload;

    const isSingleVersion = versions.length === 1;
    perfScenario = FeedPerformanceHelper.startScenario(
      pageContext,
      isSingleVersion
        ? PerfScenarios.RecycleBinPackageVersionPermanentDelete
        : PerfScenarios.MultiplePackageVersionsPermanentDeleted,
    );

    const dataService = pageContext.getService<INpmDataService>("INpmDataService");
    const iterator: IBatchIterator<MinimalPackageDetails[]> = new BatchGenerator(
      versions.map((v) => {
        return {
          id: v.name,
          version: v.version,
        } as MinimalPackageDetails;
      }),
      dataService.batchSize,
    );

    const projectId = feed.project ? feed.project.id : "";

    yield call([dataService, dataService.batchPermanentlyDelete], feed.id, iterator, projectId);

    FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    yield put(RootActions.permDeleteCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    if (perfScenario) {
      FeedPerformanceHelper.endScenario(pageContext, perfScenario);
    }
    yield put(RootActions.permDeleteCommandFailed(error.message));
  }
}

export function* setUpstreamOverride(
  action: ActionWithPayload<NpmActionTypes.SetUpstreamOverride, IUpstreamOverrideActionPayload>,
): Generator {
  try {
    yield put(PackageActions.upstreamOverrideCommandStarted());
    const { feed, packageName, allowExternalVersions, onSuccessCallback } = action.payload;

    const behavior = {
      versionsFromExternalUpstreams: allowExternalVersions
        ? UpstreamVersionVisibility.AllowExternalVersions
        : UpstreamVersionVisibility.Auto,
    } as UpstreamingBehavior;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;

    const dataService = pageContext.getService<INpmDataService>("INpmDataService");

    yield call(
      [dataService, dataService.setUpstreamingBehavior],
      feed.id,
      packageName,
      behavior,
      feed.project?.id,
    );

    yield put(PackageActions.upstreamOverrideCommandCompleted(allowExternalVersions));
    yield call(onSuccessCallback);
  } catch (error) {
    yield put(PackageActions.upstreamOverrideCommandFailed(error.message));
  }
}

export function* softDelete(
  action: ActionWithPayload<NpmActionTypes.SoftDelete, IDeleteActionPayload>,
): Generator {
  try {
    yield put(RootActions.softDeleteCommandStarted());
    const { feed, versions, onSuccessCallback } = action.payload;

    const pageContext: IVssPageContext = (yield getContext("pageContext")) as any;
    const dataService = pageContext.getService<INpmDataService>("INpmDataService");

    const iterator = new TransformingBatchGenerator(
      versions,
      dataService.batchSize,
      (version: IPackageVersionIdentity) => {
        return {
          id: version.name,
          version: version.version,
        };
      },
    );

    yield call([dataService, dataService.batchDelete], feed.id, iterator, feed.project?.id);

    yield put(RootActions.softDeleteCommandCompleted());
    yield call(onSuccessCallback, versions);
  } catch (error) {
    yield put(RootActions.softDeleteCommandFailed(error.message));
  }
}
